import { useReadContract } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { PRESALE_ABI, PRESALE_ADDRESS, USDT_DECIMALS } from '../lib/contracts';

/**
 * Reads stats from the AIMLPreSale contract.
 *
 * IMPORTANT — DECIMAL QUIRK:
 *   The contract's `getTokenAmountUSDT(amountUSDT)` function takes raw 6-decimal
 *   USDT input but returns AIML in WHOLE token units (no 18-decimal scaling).
 *   Example: getTokenAmountUSDT(100_000_000) (= 100 USDT raw) → 849978
 *   Means: 100 USDT buys ~849,978 AIML. We use Number(value) directly, not formatUnits.
 *
 *   `usdRaised()` does follow the standard 6-decimal USDT convention.
 */
export function usePresaleStats() {
  // tokens per 1 USDT — live rate from the contract
  const oneUsdtRaw = parseUnits('1', USDT_DECIMALS);
  const rate = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'getTokenAmountUSDT',
    args: [oneUsdtRaw],
  });

  const totalRaised = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'usdRaised',
  });

  const paused = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'paused',
  });

  // Approximate AIML sold: getTokenAmountUSDT(usdRaised). The contract returns
  // whole tokens at the current rate; if usdRate has changed over the sale, this
  // is an estimate, not an exact historical sum.
  const totalSold = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'getTokenAmountUSDT',
    args: totalRaised.data !== undefined ? [totalRaised.data as bigint] : undefined,
    query: {
      enabled: totalRaised.data !== undefined,
    },
  });

  return {
    /** AIML per 1 USDT (whole tokens, e.g. 8499). */
    tokensPerUsdt: rate.data !== undefined ? Number(rate.data as bigint) : null,

    /** Total AIML sold (approximate, whole tokens). */
    totalAimlSold: totalSold.data !== undefined ? Number(totalSold.data as bigint) : null,

    /** Total USDT raised (formatted from 6-decimal raw). */
    totalUsdtRaised:
      totalRaised.data !== undefined
        ? Number(formatUnits(totalRaised.data as bigint, USDT_DECIMALS))
        : null,

    paused: paused.data === true,

    isLoading:
      rate.isLoading || totalSold.isLoading || totalRaised.isLoading || paused.isLoading,
    error: rate.error ?? totalSold.error ?? totalRaised.error ?? paused.error,
    refetch: () => {
      rate.refetch();
      totalRaised.refetch();
      totalSold.refetch();
      paused.refetch();
    },
  };
}
